import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart } from 'recharts';
import { Calendar, Filter, Download, RefreshCw, Package, TrendingUp, TrendingDown, Archive, FileText, ChevronDown, ArrowDownToLine, ArrowUpFromLine, Award, Layers, Search, X, Eye, FileSpreadsheet, AlertTriangle } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as XLSX from 'xlsx';
import authUtils from '../utils/authUtils';

// Convert a date value (Date, ISO string, etc.) to a local 'yyyy-mm-dd' string.
// Avoids UTC parsing shifting the date by a day relative to the input's local time.
const toLocalDateStr = (value) => {
  if (!value) return '';
  const dateObj = value instanceof Date ? value : new Date(value);
  if (isNaN(dateObj.getTime())) return '';
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const BaoCaoKho = () => {
  // Helper function to get default date range (first day of month to today)
  const getDefaultDateRange = () => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return {
      from: formatDate(firstDayOfMonth),
      to: formatDate(today)
    };
  };

  const defaultDates = getDefaultDateRange();

  // State Management
  const [phieuList, setPhieuList] = useState([]);
  const [chiTietList, setChiTietList] = useState([]);
  const [dmhh, setDmhh] = useState([]);
  const [giaBanList, setGiaBanList] = useState([]);

  // Domino data states
  const [phieuListDomino, setPhieuListDomino] = useState([]);
  const [chiTietListDomino, setChiTietListDomino] = useState([]);

  // Filter states - Khởi tạo với giá trị mặc định
  const [filterDateFrom, setFilterDateFrom] = useState(defaultDates.from);
  const [filterDateTo, setFilterDateTo] = useState(defaultDates.to);

  // Dimension filter states
  const [filterDoDay, setFilterDoDay] = useState([]);
  const [filterTieuChuan, setFilterTieuChuan] = useState([]);
  const [filterDai, setFilterDai] = useState([]);
  const [filterRong, setFilterRong] = useState([]);
  const [filterMaKien, setFilterMaKien] = useState('');
  // Phân trang tab "Chi tiết theo kiện"
  const [kienPage, setKienPage] = useState(1);
  const [kienPerPage, setKienPerPage] = useState(50);
  const [showDoDayDropdown, setShowDoDayDropdown] = useState(false);
  const [showTieuChuanDropdown, setShowTieuChuanDropdown] = useState(false);
  const [showDaiDropdown, setShowDaiDropdown] = useState(false);
  const [showRongDropdown, setShowRongDropdown] = useState(false);
  const [searchDoDay, setSearchDoDay] = useState('');
  const [searchTieuChuan, setSearchTieuChuan] = useState('');
  const [searchDai, setSearchDai] = useState('');
  const [searchRong, setSearchRong] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [showFilter, setShowFilter] = useState(true);
  const [activeTab, setActiveTab] = useState('tong-hop');
  const [activeTop10Tab, setActiveTop10Tab] = useState('nhap');
  const [expandedDoDayRows, setExpandedDoDayRows] = useState({});

  // Refs
  const doDayDropdownRef = useRef(null);
  const tieuChuanDropdownRef = useRef(null);
  const daiDropdownRef = useRef(null);
  const rongDropdownRef = useRef(null);

  // Report data states
  const [tongHopData, setTongHopData] = useState({
    tonDauKy: { kien: 0, m3: 0, giaTri: 0 },
    nhapTrongKy: { kien: 0, m3: 0, giaTri: 0 },
    xuatTrongKy: { kien: 0, m3: 0, giaTri: 0 },
    tonCuoiKy: { kien: 0, m3: 0, giaTri: 0 }
  });
  const [tonTheoNhomHang, setTonTheoNhomHang] = useState([]);
  const [tonTheoKien, setTonTheoKien] = useState([]);
  const [tonTheoDoDay, setTonTheoDoDay] = useState([]);
  const [top10Data, setTop10Data] = useState({
    nhapNhieu: [],
    xuatNhieu: [],
    tonNhieu: []
  });

  // Domino report data states
  const [tongHopDataDomino, setTongHopDataDomino] = useState({
    tonDauKy: { kien: 0, kl: 0 },
    nhapTrongKy: { kien: 0, kl: 0 },
    xuatTrongKy: { kien: 0, kl: 0 },
    tonCuoiKy: { kien: 0, kl: 0 }
  });
  const [tonTheoDominoKien, setTonTheoDominoKien] = useState([]);
  const [dongXuatCanKiemTra, setDongXuatCanKiemTra] = useState([]);

  const COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#14b8a6', '#f97316', '#06b6d4', '#84cc16'];

  // Get unique values for filters
  const uniqueDoDay = [...new Set(chiTietList.map(item => item['DAY']).filter(Boolean))].sort((a, b) => parseFloat(a) - parseFloat(b));
  const uniqueTieuChuan = [...new Set(chiTietList.map(item => item['TIEU_CHUAN']).filter(Boolean))];
  const uniqueDai = [...new Set(chiTietList.map(item => item['DAI']).filter(Boolean))].sort((a, b) => parseFloat(a) - parseFloat(b));
  const uniqueRong = [...new Set(chiTietList.map(item => item['RONG']).filter(Boolean))].sort((a, b) => parseFloat(a) - parseFloat(b));

  // Filter do day based on search
  const filteredDoDay = uniqueDoDay.filter(doDay =>
    doDay.toString().toLowerCase().includes(searchDoDay.toLowerCase())
  );

  // Filter tieu chuan based on search
  const filteredTieuChuan = uniqueTieuChuan.filter(tieuChuan =>
    tieuChuan.toLowerCase().includes(searchTieuChuan.toLowerCase())
  );

  // Filter dai based on search
  const filteredDai = uniqueDai.filter(dai =>
    dai.toString().toLowerCase().includes(searchDai.toLowerCase())
  );

  // Filter rong based on search
  const filteredRong = uniqueRong.filter(rong =>
    rong.toString().toLowerCase().includes(searchRong.toLowerCase())
  );

  // Get display text for selected filters
  const getSelectedDoDayText = () => {
    if (filterDoDay.length === 0) return 'Tất cả độ dày';
    if (filterDoDay.length === 1) return `${filterDoDay[0]}mm`;
    return `Đã chọn ${filterDoDay.length} độ dày`;
  };

  const getSelectedTieuChuanText = () => {
    if (filterTieuChuan.length === 0) return 'Tất cả chất lượng';
    if (filterTieuChuan.length === 1) return filterTieuChuan[0];
    return `Đã chọn ${filterTieuChuan.length} tiêu chuẩn`;
  };

  const getSelectedDaiText = () => {
    if (filterDai.length === 0) return 'Tất cả dài';
    if (filterDai.length === 1) return `${filterDai[0]}mm`;
    return `Đã chọn ${filterDai.length} độ dài`;
  };

  const getSelectedRongText = () => {
    if (filterRong.length === 0) return 'Tất cả rộng';
    if (filterRong.length === 1) return `${filterRong[0]}mm`;
    return `Đã chọn ${filterRong.length} độ rộng`;
  };

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (doDayDropdownRef.current && !doDayDropdownRef.current.contains(event.target)) {
        setShowDoDayDropdown(false);
      }
      if (tieuChuanDropdownRef.current && !tieuChuanDropdownRef.current.contains(event.target)) {
        setShowTieuChuanDropdown(false);
      }
      if (daiDropdownRef.current && !daiDropdownRef.current.contains(event.target)) {
        setShowDaiDropdown(false);
      }
      if (rongDropdownRef.current && !rongDropdownRef.current.contains(event.target)) {
        setShowRongDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch data
  const fetchData = async (showToast = false) => {
    try {
      setIsLoading(true);
      const [phieuResponse, chiTietResponse, dmhhResponse, giaBanResponse, phieuDominoResponse, chiTietDominoResponse] = await Promise.all([
        authUtils.apiRequestKHO('XUATNHAPKHO', 'Find', {}),
        authUtils.apiRequestKHO('XUATNHAPKHO_CHITIET', 'Find', {}),
        authUtils.apiRequestKHO('DMHH', 'Find', {}),
        authUtils.apiRequestKHO('CD_GIABAN', 'Find', {}),
        authUtils.apiRequestKHO('XUATNHAPKHO_DOMINO', 'Find', {}),
        authUtils.apiRequestKHO('XUATNHAPKHO_CHITIET_DOMINO', 'Find', {})
      ]);

      setPhieuList(phieuResponse);
      setChiTietList(chiTietResponse);
      setDmhh(dmhhResponse);
      setGiaBanList(giaBanResponse);
      setPhieuListDomino(phieuDominoResponse);
      setChiTietListDomino(chiTietDominoResponse);

      if (showToast) {
        toast.success('Tải dữ liệu thành công');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Lỗi khi tải dữ liệu');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Cache đơn giá theo (DAY, TIEU_CHUAN) — reset khi bảng giá thay đổi.
  // Trước đây mỗi kiện đều quét + parse ngày toàn bộ giaBanList.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const donGiaCache = useMemo(() => new Map(), [giaBanList]);

  // Helper function to get unit price for a given DAY and TIEU_CHUAN
  const getDonGiaUocLuong = (day, tieuChuan) => {
    if (!day || !tieuChuan) return 0;
    const cacheKey = `${parseFloat(day)}|${tieuChuan}`;
    if (donGiaCache.has(cacheKey)) return donGiaCache.get(cacheKey);
    const donGia = tinhDonGiaUocLuong(day, tieuChuan);
    donGiaCache.set(cacheKey, donGia);
    return donGia;
  };

  const tinhDonGiaUocLuong = (day, tieuChuan) => {

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Find matching price records with DONGIA_UOC_LUONG
    const matchingPrices = giaBanList.filter(item => {
      // Check if this is an estimated price record (has DONGIA_UOC_LUONG)
      if (!item['DONGIA_UOC_LUONG'] || item['DONGIA_UOC_LUONG'] === '') return false;

      // Check if DAY and TIEU_CHUAN match
      if (parseFloat(item['DAY']) !== parseFloat(day)) return false;
      if (item['TIEU_CHUAN'] !== tieuChuan) return false;

      // Check if price is currently active
      const startDate = item['HIEULUC_TU'] ? new Date(item['HIEULUC_TU']) : null;
      const endDate = item['HIEULUC_DEN'] ? new Date(item['HIEULUC_DEN']) : null;

      if (startDate) startDate.setHours(0, 0, 0, 0);
      if (endDate) endDate.setHours(0, 0, 0, 0);

      // Check if current date is within validity period
      if (startDate && today < startDate) return false;
      if (endDate && today > endDate) return false;

      return true;
    });

    // Return the most recent active price
    if (matchingPrices.length > 0) {
      // Sort by HIEULUC_TU descending to get the most recent
      matchingPrices.sort((a, b) => {
        const dateA = new Date(a['HIEULUC_TU']);
        const dateB = new Date(b['HIEULUC_TU']);
        return dateB - dateA;
      });
      return parseFloat(matchingPrices[0]['DONGIA_UOC_LUONG']) || 0;
    }

    return 0;
  };

  // Calculate reports
  useEffect(() => {
    if (chiTietList.length > 0) {
      calculateReports();
    }
  }, [chiTietList, phieuList, filterDateFrom, filterDateTo, filterDoDay, filterTieuChuan, filterDai, filterRong]);

  // Calculate Domino reports
  useEffect(() => {
    if (chiTietListDomino.length > 0) {
      calculateDominoReports();
    }
  }, [chiTietListDomino, phieuListDomino, filterDateFrom, filterDateTo]);

  // Calculate Top 10
  const calculateTop10 = (data) => {
    // Top 10 Nhập nhiều nhất
    const nhapNhieu = [...data]
      .sort((a, b) => b.nhap.m3 - a.nhap.m3)
      .slice(0, 10)
      .map(item => ({
        nhomHang: item.nhomHang,
        tieuChuan: item.tieuChuan,
        kien: item.nhap.kien,
        m3: item.nhap.m3
      }));

    // Top 10 Xuất nhiều nhất
    const xuatNhieu = [...data]
      .sort((a, b) => b.xuat.m3 - a.xuat.m3)
      .slice(0, 10)
      .map(item => ({
        nhomHang: item.nhomHang,
        tieuChuan: item.tieuChuan,
        kien: item.xuat.kien,
        m3: item.xuat.m3
      }));

    // Top 10 Tồn nhiều nhất
    const tonNhieu = [...data]
      .sort((a, b) => b.tonCuoiKy.m3 - a.tonCuoiKy.m3)
      .slice(0, 10)
      .map(item => ({
        nhomHang: item.nhomHang,
        tieuChuan: item.tieuChuan,
        kien: item.tonCuoiKy.kien,
        m3: item.tonCuoiKy.m3
      }));

    setTop10Data({ nhapNhieu, xuatNhieu, tonNhieu });
  };

  const calculateReports = () => {
    // Tìm các dòng xuất kho bất thường để nhắc người dùng kiểm tra lại:
    // 1) Cùng một mã kiện bị ghi xuất nhiều lần
    // 2) Xuất một mã kiện chưa từng được nhập kho
    const xuatTheoKien = {};
    chiTietList.forEach(item => {
      if (item['NGHIEP_VU'] === 'XUAT') {
        const maKien = item['MA_KIEN'];
        if (!xuatTheoKien[maKien]) xuatTheoKien[maKien] = [];
        xuatTheoKien[maKien].push(item);
      }
    });
    const maKienDaNhap = new Set(chiTietList.filter(item => item['NGHIEP_VU'] === 'NHAP').map(item => item['MA_KIEN']));

    const dongCanKiemTra = [];
    Object.entries(xuatTheoKien).forEach(([maKien, dongXuat]) => {
      if (dongXuat.length > 1) {
        dongXuat.forEach(item => dongCanKiemTra.push({
          ...item,
          _loi: `Xuất trùng ${dongXuat.length} lần`,
          _huongDan: `Kiện này bị ghi xuất ${dongXuat.length} lần, chỉ giữ lại 1 dòng`
        }));
      } else if (!maKienDaNhap.has(maKien)) {
        dongCanKiemTra.push({
          ...dongXuat[0],
          _loi: 'Chưa từng nhập kho',
          _huongDan: 'Không tìm thấy phiếu nhập nào của kiện này'
        });
      }
    });
    setDongXuatCanKiemTra(dongCanKiemTra);

    // Phân loại dữ liệu theo thời gian
    const dataTruocKy = [];
    const dataTrongKy = [];
    const dataSauKy = [];

    chiTietList.forEach(item => {
      const itemDateStr = toLocalDateStr(item['NGAY_NHAP_XUAT']);

      if (!itemDateStr) return;

      if (filterDateFrom && itemDateStr < filterDateFrom) {
        dataTruocKy.push(item);
      } else if ((!filterDateFrom || itemDateStr >= filterDateFrom) && (!filterDateTo || itemDateStr <= filterDateTo)) {
        dataTrongKy.push(item);
      } else if (filterDateTo && itemDateStr > filterDateTo) {
        dataSauKy.push(item);
      }
    });

    // Filter by all dimension filters
    const filterByDimensions = (data) => {
      let filtered = data;
      if (filterDoDay.length > 0) {
        filtered = filtered.filter(item => filterDoDay.includes(item['DAY']));
      }
      if (filterTieuChuan.length > 0) {
        filtered = filtered.filter(item => filterTieuChuan.includes(item['TIEU_CHUAN']));
      }
      if (filterDai.length > 0) {
        filtered = filtered.filter(item => filterDai.includes(item['DAI']));
      }
      if (filterRong.length > 0) {
        filtered = filtered.filter(item => filterRong.includes(item['RONG']));
      }
      return filtered;
    };

    const dataTruocKyFiltered = filterByDimensions(dataTruocKy);
    const dataTrongKyFiltered = filterByDimensions(dataTrongKy);

    // Tính tồn đầu kỳ (từ đầu đến trước kỳ báo cáo)
    const tonDauKy = calculateTonKho(dataTruocKyFiltered);

    // Tính nhập xuất trong kỳ
    const nhapXuatTrongKy = calculateNhapXuat(dataTrongKyFiltered);

    // Tính tồn cuối kỳ theo map mã kiện trên toàn bộ dữ liệu đến hết kỳ,
    // thay vì lấy tồn đầu kỳ + nhập - xuất (cách cũ bị sai khi có dòng XUAT thừa
    // hoặc XUAT không có NHAP tương ứng, và cho kết quả khác nhau tùy có lọc "Từ ngày" hay không).
    const tonCuoiKy = calculateTonKho([...dataTruocKyFiltered, ...dataTrongKyFiltered]);

    // Set tổng hợp data
    setTongHopData({
      tonDauKy: tonDauKy,
      nhapTrongKy: nhapXuatTrongKy.nhap,
      xuatTrongKy: nhapXuatTrongKy.xuat,
      tonCuoiKy: tonCuoiKy
    });

    // Tính tồn theo nhóm hàng
    const nhomHangData = calculateTonTheoNhomHang(dataTruocKyFiltered, dataTrongKyFiltered);
    setTonTheoNhomHang(nhomHangData);

    // Calculate Top 10
    calculateTop10(nhomHangData);

    // Tính tồn theo kiện
    calculateTonTheoKien(dataTruocKyFiltered, dataTrongKyFiltered);

    // Tính tồn theo độ dày
    calculateTonTheoDoDay(dataTruocKyFiltered, dataTrongKyFiltered);
  };

  // Duyệt theo map MA_KIEN: nhập thì thêm kiện vào kho, xuất thì lấy kiện ra.
  // Dòng XUAT không tìm thấy mã kiện trong map (xuất trùng, hoặc xuất mã kiện chưa từng nhập)
  // được gom riêng vào xuatKhongKhop - hàng đã thực sự ra khỏi kho nên vẫn phải trừ vào tồn.
  const buildTonKho = (data) => {
    const tonKhoMap = {};
    const xuatKhongKhop = [];

    data.forEach(item => {
      const maKien = item['MA_KIEN'];
      const nghiepVu = item['NGHIEP_VU'];

      if (nghiepVu === 'NHAP') {
        tonKhoMap[maKien] = item;
      } else if (nghiepVu === 'XUAT') {
        if (tonKhoMap[maKien]) {
          delete tonKhoMap[maKien];
        } else {
          xuatKhongKhop.push(item);
        }
      }
    });

    return { tonList: Object.values(tonKhoMap), xuatKhongKhop };
  };

  const calculateTonKho = (data) => {
    const { tonList, xuatKhongKhop } = buildTonKho(data);
    const tongM3 = (list) => list.reduce((sum, item) => sum + (parseFloat(item['SO_KHOI']) || 0), 0);
    const tongGiaTri = (list) => list.reduce((sum, item) => {
      const soKhoi = parseFloat(item['SO_KHOI']) || 0;
      const donGia = getDonGiaUocLuong(item['DAY'], item['TIEU_CHUAN']);
      return sum + donGia * soKhoi;
    }, 0);

    return {
      kien: tonList.length - xuatKhongKhop.length,
      m3: tongM3(tonList) - tongM3(xuatKhongKhop),
      giaTri: tongGiaTri(tonList) - tongGiaTri(xuatKhongKhop)
    };
  };

  const calculateNhapXuat = (data) => {
    let nhapKien = 0, nhapM3 = 0, nhapGiaTri = 0;
    let xuatKien = 0, xuatM3 = 0, xuatGiaTri = 0;

    data.forEach(item => {
      const nghiepVu = item['NGHIEP_VU'];
      const soKhoi = parseFloat(item['SO_KHOI']) || 0;
      const donGia = getDonGiaUocLuong(item['DAY'], item['TIEU_CHUAN']);

      if (nghiepVu === 'NHAP') {
        nhapKien += 1;
        nhapM3 += soKhoi;
        nhapGiaTri += donGia * soKhoi;
      } else if (nghiepVu === 'XUAT') {
        xuatKien += 1;
        xuatM3 += soKhoi;
        xuatGiaTri += donGia * soKhoi;
      }
    });

    return {
      nhap: { kien: nhapKien, m3: nhapM3, giaTri: nhapGiaTri },
      xuat: { kien: xuatKien, m3: xuatM3, giaTri: xuatGiaTri }
    };
  };

  const calculateTonTheoNhomHang = (dataTruocKy, dataTrongKy) => {
    const nhomHangMap = {};

    // Tính tồn đầu kỳ theo nhóm hàng
    const tonDauKyMap = {};
    dataTruocKy.forEach(item => {
      const maKien = item['MA_KIEN'];
      const nghiepVu = item['NGHIEP_VU'];

      if (nghiepVu === 'NHAP') {
        tonDauKyMap[maKien] = item;
      } else if (nghiepVu === 'XUAT') {
        delete tonDauKyMap[maKien];
      }
    });

    Object.values(tonDauKyMap).forEach(item => {
      const nhomHang = item['NHOM_HANG'] || 'Không xác định';
      const tieuChuan = item['TIEU_CHUAN'] || 'Không xác định';
      const key = `${nhomHang}_${tieuChuan}`;

      if (!nhomHangMap[key]) {
        nhomHangMap[key] = {
          nhomHang,
          tieuChuan,
          tonDauKy: { kien: 0, m3: 0 },
          nhap: { kien: 0, m3: 0 },
          xuat: { kien: 0, m3: 0 },
          tonCuoiKy: { kien: 0, m3: 0 }
        };
      }

      nhomHangMap[key].tonDauKy.kien += 1;
      nhomHangMap[key].tonDauKy.m3 += parseFloat(item['SO_KHOI']) || 0;
    });

    // Tính nhập xuất trong kỳ theo nhóm hàng
    dataTrongKy.forEach(item => {
      const nhomHang = item['NHOM_HANG'] || 'Không xác định';
      const tieuChuan = item['TIEU_CHUAN'] || 'Không xác định';
      const key = `${nhomHang}_${tieuChuan}`;
      const nghiepVu = item['NGHIEP_VU'];
      const soKhoi = parseFloat(item['SO_KHOI']) || 0;

      if (!nhomHangMap[key]) {
        nhomHangMap[key] = {
          nhomHang,
          tieuChuan,
          tonDauKy: { kien: 0, m3: 0 },
          nhap: { kien: 0, m3: 0 },
          xuat: { kien: 0, m3: 0 },
          tonCuoiKy: { kien: 0, m3: 0 }
        };
      }

      if (nghiepVu === 'NHAP') {
        nhomHangMap[key].nhap.kien += 1;
        nhomHangMap[key].nhap.m3 += soKhoi;
      } else if (nghiepVu === 'XUAT') {
        nhomHangMap[key].xuat.kien += 1;
        nhomHangMap[key].xuat.m3 += soKhoi;
      }
    });

    // Tính tồn cuối kỳ theo map mã kiện trên toàn bộ dữ liệu đến hết kỳ,
    // đồng thời trừ các dòng xuất không khớp mã kiện (hàng vẫn đã ra khỏi kho)
    const { tonList: tonCuoiKyList, xuatKhongKhop } = buildTonKho([...dataTruocKy, ...dataTrongKy]);

    const capNhatTonCuoiKy = (item, dau) => {
      const nhomHang = item['NHOM_HANG'] || 'Không xác định';
      const tieuChuan = item['TIEU_CHUAN'] || 'Không xác định';
      const key = `${nhomHang}_${tieuChuan}`;

      if (!nhomHangMap[key]) {
        nhomHangMap[key] = {
          nhomHang,
          tieuChuan,
          tonDauKy: { kien: 0, m3: 0 },
          nhap: { kien: 0, m3: 0 },
          xuat: { kien: 0, m3: 0 },
          tonCuoiKy: { kien: 0, m3: 0 }
        };
      }

      nhomHangMap[key].tonCuoiKy.kien += dau;
      nhomHangMap[key].tonCuoiKy.m3 += dau * (parseFloat(item['SO_KHOI']) || 0);
    };

    tonCuoiKyList.forEach(item => capNhatTonCuoiKy(item, 1));
    xuatKhongKhop.forEach(item => capNhatTonCuoiKy(item, -1));

    return Object.values(nhomHangMap);
  };

  const calculateTonTheoKien = (dataTruocKy, dataTrongKy) => {
    const tonKhoMap = {};

    // Tính tồn đầu kỳ
    dataTruocKy.forEach(item => {
      const maKien = item['MA_KIEN'];
      const nghiepVu = item['NGHIEP_VU'];

      if (nghiepVu === 'NHAP') {
        tonKhoMap[maKien] = { ...item, trangThai: 'TON_DAU_KY' };
      } else if (nghiepVu === 'XUAT') {
        delete tonKhoMap[maKien];
      }
    });

    // Xử lý nhập xuất trong kỳ
    dataTrongKy.forEach(item => {
      const maKien = item['MA_KIEN'];
      const nghiepVu = item['NGHIEP_VU'];

      if (nghiepVu === 'NHAP') {
        tonKhoMap[maKien] = { ...item, trangThai: 'NHAP_TRONG_KY' };
      } else if (nghiepVu === 'XUAT') {
        delete tonKhoMap[maKien];
      }
    });

    // Chỉ lấy các kiện còn tồn và thêm thông tin giá
    const tonKhoList = Object.values(tonKhoMap).map(item => {
      const donGia = getDonGiaUocLuong(item['DAY'], item['TIEU_CHUAN']);
      const soKhoi = parseFloat(item['SO_KHOI']) || 0;
      const giaTri = donGia * soKhoi;

      return {
        ...item,
        DON_GIA_UOC_LUONG: donGia,
        GIA_TRI_UOC_LUONG: giaTri
      };
    });

    setTonTheoKien(tonKhoList);
  };

  // Hàm mới: Tính tồn theo độ dày
  const calculateTonTheoDoDay = (dataTruocKy, dataTrongKy) => {
    const doDayMap = {};

    // Tính tồn đầu kỳ theo độ dày và chất lượng
    const tonDauKyMap = {};
    dataTruocKy.forEach(item => {
      const maKien = item['MA_KIEN'];
      const nghiepVu = item['NGHIEP_VU'];

      if (nghiepVu === 'NHAP') {
        tonDauKyMap[maKien] = item;
      } else if (nghiepVu === 'XUAT') {
        delete tonDauKyMap[maKien];
      }
    });

    Object.values(tonDauKyMap).forEach(item => {
      const doDay = item['DAY'] || 'Không xác định';
      const tieuChuan = item['TIEU_CHUAN'] || 'Không xác định';

      if (!doDayMap[doDay]) {
        doDayMap[doDay] = {
          doDay,
          tonDauKy: { kien: 0, m3: 0 },
          nhap: { kien: 0, m3: 0 },
          xuat: { kien: 0, m3: 0 },
          tonCuoiKy: { kien: 0, m3: 0, giaTri: 0 },
          subGroups: {}
        };
      }

      // Cập nhật tổng
      doDayMap[doDay].tonDauKy.kien += 1;
      doDayMap[doDay].tonDauKy.m3 += parseFloat(item['SO_KHOI']) || 0;

      // Cập nhật sub-group theo chất lượng
      if (!doDayMap[doDay].subGroups[tieuChuan]) {
        doDayMap[doDay].subGroups[tieuChuan] = {
          tieuChuan,
          tonDauKy: { kien: 0, m3: 0 },
          nhap: { kien: 0, m3: 0 },
          xuat: { kien: 0, m3: 0 },
          tonCuoiKy: { kien: 0, m3: 0, giaTri: 0 }
        };
      }
      doDayMap[doDay].subGroups[tieuChuan].tonDauKy.kien += 1;
      doDayMap[doDay].subGroups[tieuChuan].tonDauKy.m3 += parseFloat(item['SO_KHOI']) || 0;
    });

    // Tính nhập xuất trong kỳ theo độ dày và chất lượng
    dataTrongKy.forEach(item => {
      const doDay = item['DAY'] || 'Không xác định';
      const tieuChuan = item['TIEU_CHUAN'] || 'Không xác định';
      const nghiepVu = item['NGHIEP_VU'];
      const soKhoi = parseFloat(item['SO_KHOI']) || 0;

      if (!doDayMap[doDay]) {
        doDayMap[doDay] = {
          doDay,
          tonDauKy: { kien: 0, m3: 0 },
          nhap: { kien: 0, m3: 0 },
          xuat: { kien: 0, m3: 0 },
          tonCuoiKy: { kien: 0, m3: 0, giaTri: 0 },
          subGroups: {}
        };
      }

      if (!doDayMap[doDay].subGroups[tieuChuan]) {
        doDayMap[doDay].subGroups[tieuChuan] = {
          tieuChuan,
          tonDauKy: { kien: 0, m3: 0 },
          nhap: { kien: 0, m3: 0 },
          xuat: { kien: 0, m3: 0 },
          tonCuoiKy: { kien: 0, m3: 0, giaTri: 0 }
        };
      }

      if (nghiepVu === 'NHAP') {
        doDayMap[doDay].nhap.kien += 1;
        doDayMap[doDay].nhap.m3 += soKhoi;
        doDayMap[doDay].subGroups[tieuChuan].nhap.kien += 1;
        doDayMap[doDay].subGroups[tieuChuan].nhap.m3 += soKhoi;
      } else if (nghiepVu === 'XUAT') {
        doDayMap[doDay].xuat.kien += 1;
        doDayMap[doDay].xuat.m3 += soKhoi;
        doDayMap[doDay].subGroups[tieuChuan].xuat.kien += 1;
        doDayMap[doDay].subGroups[tieuChuan].xuat.m3 += soKhoi;
      }
    });

    // Tính tồn cuối kỳ theo map mã kiện trên toàn bộ dữ liệu đến hết kỳ,
    // đồng thời trừ các dòng xuất không khớp mã kiện (hàng vẫn đã ra khỏi kho)
    const { tonList: tonCuoiKyList, xuatKhongKhop } = buildTonKho([...dataTruocKy, ...dataTrongKy]);

    const capNhatTonCuoiKy = (item, dau) => {
      const doDay = item['DAY'] || 'Không xác định';
      const tieuChuan = item['TIEU_CHUAN'] || 'Không xác định';
      const soKhoi = parseFloat(item['SO_KHOI']) || 0;

      if (!doDayMap[doDay]) {
        doDayMap[doDay] = {
          doDay,
          tonDauKy: { kien: 0, m3: 0 },
          nhap: { kien: 0, m3: 0 },
          xuat: { kien: 0, m3: 0 },
          tonCuoiKy: { kien: 0, m3: 0, giaTri: 0 },
          subGroups: {}
        };
      }

      if (!doDayMap[doDay].subGroups[tieuChuan]) {
        doDayMap[doDay].subGroups[tieuChuan] = {
          tieuChuan,
          tonDauKy: { kien: 0, m3: 0 },
          nhap: { kien: 0, m3: 0 },
          xuat: { kien: 0, m3: 0 },
          tonCuoiKy: { kien: 0, m3: 0, giaTri: 0 }
        };
      }

      doDayMap[doDay].tonCuoiKy.kien += dau;
      doDayMap[doDay].tonCuoiKy.m3 += dau * soKhoi;
      doDayMap[doDay].subGroups[tieuChuan].tonCuoiKy.kien += dau;
      doDayMap[doDay].subGroups[tieuChuan].tonCuoiKy.m3 += dau * soKhoi;
    };

    tonCuoiKyList.forEach(item => capNhatTonCuoiKy(item, 1));
    xuatKhongKhop.forEach(item => capNhatTonCuoiKy(item, -1));

    // Tính giá trị ước lượng
    Object.keys(doDayMap).forEach(key => {
      const item = doDayMap[key];

      // Tính cho từng sub-group
      let totalGiaTri = 0;
      Object.keys(item.subGroups).forEach(tieuChuan => {
        const subGroup = item.subGroups[tieuChuan];

        // Lấy đơn giá ước lượng
        const donGia = getDonGiaUocLuong(item.doDay, tieuChuan);
        subGroup.donGia = donGia; // Lưu đơn giá
        subGroup.tonCuoiKy.giaTri = donGia * subGroup.tonCuoiKy.m3;
        totalGiaTri += subGroup.tonCuoiKy.giaTri;
      });

      item.tonCuoiKy.giaTri = totalGiaTri;

      // Chuyển subGroups thành mảng để dễ dàng render
      item.subGroupsArray = Object.values(item.subGroups);
    });

    // Sắp xếp theo độ dày (số)
    const sortedData = Object.values(doDayMap).sort((a, b) => {
      const doDayA = parseFloat(a.doDay) || 0;
      const doDayB = parseFloat(b.doDay) || 0;
      return doDayA - doDayB;
    });

    setTonTheoDoDay(sortedData);
  };

  // Calculate Domino reports
  const calculateDominoReports = () => {
    // Phân loại dữ liệu theo thời gian
    const dataTruocKy = [];
    const dataTrongKy = [];

    chiTietListDomino.forEach(item => {
      const itemDateStr = toLocalDateStr(item['NGAY_NHAP_XUAT']);

      if (!itemDateStr) return;

      if (filterDateFrom && itemDateStr < filterDateFrom) {
        dataTruocKy.push(item);
      } else if ((!filterDateFrom || itemDateStr >= filterDateFrom) && (!filterDateTo || itemDateStr <= filterDateTo)) {
        dataTrongKy.push(item);
      }
    });

    // Tính tồn đầu kỳ
    let tonDauKyKien = 0;
    let tonDauKyKL = 0;

    dataTruocKy.forEach(item => {
      const nghiepVu = item['NGHIEP_VU'];
      const soKien = parseFloat(item['SO KIEN']) || 0;
      const tongKg = parseFloat(item['TONG KG UL']) || 0;

      if (nghiepVu === 'NHAP') {
        tonDauKyKien += soKien;
        tonDauKyKL += tongKg;
      } else if (nghiepVu === 'XUAT') {
        tonDauKyKien -= soKien;
        tonDauKyKL -= tongKg;
      }
    });

    const tonDauKy = {
      kien: tonDauKyKien,
      kl: tonDauKyKL
    };

    // Tính nhập xuất trong kỳ
    let nhapKien = 0, nhapKL = 0;
    let xuatKien = 0, xuatKL = 0;

    dataTrongKy.forEach(item => {
      const nghiepVu = item['NGHIEP_VU'];
      const soKien = parseFloat(item['SO KIEN']) || 0;
      const tongKg = parseFloat(item['TONG KG UL']) || 0;

      if (nghiepVu === 'NHAP') {
        nhapKien += soKien;
        nhapKL += tongKg;
      } else if (nghiepVu === 'XUAT') {
        xuatKien += soKien;
        xuatKL += tongKg;
      }
    });

    // Tính tồn cuối kỳ
    const tonCuoiKy = {
      kien: tonDauKy.kien + nhapKien - xuatKien,
      kl: tonDauKy.kl + nhapKL - xuatKL
    };

    setTongHopDataDomino({
      tonDauKy,
      nhapTrongKy: { kien: nhapKien, kl: nhapKL },
      xuatTrongKy: { kien: xuatKien, kl: xuatKL },
      tonCuoiKy
    });

    // Chi tiết: hiển thị tất cả các dòng trong kỳ
    setTonTheoDominoKien(dataTrongKy);
  };

  // Filter tonTheoKien based on selected filters — tính 1 lần mỗi khi dữ liệu/bộ lọc đổi
  // (trước đây được gọi lại 6 lần trong mỗi lần render)
  const filteredTonTheoKien = useMemo(() => {
    let filtered = [...tonTheoKien];

    // Filter by do day
    if (filterDoDay.length > 0) {
      filtered = filtered.filter(item => filterDoDay.includes(item['DAY']));
    }

    // Filter by tieu chuan
    if (filterTieuChuan.length > 0) {
      filtered = filtered.filter(item => filterTieuChuan.includes(item['TIEU_CHUAN']));
    }

    // Filter by dai
    if (filterDai.length > 0) {
      filtered = filtered.filter(item => filterDai.includes(item['DAI']));
    }

    // Filter by rong
    if (filterRong.length > 0) {
      filtered = filtered.filter(item => filterRong.includes(item['RONG']));
    }

    // Filter by ma kien
    if (filterMaKien) {
      filtered = filtered.filter(item =>
        item['MA_KIEN'].toLowerCase().includes(filterMaKien.toLowerCase())
      );
    }

    return filtered;
  }, [tonTheoKien, filterDoDay, filterTieuChuan, filterDai, filterRong, filterMaKien]);
  const getFilteredTonTheoKien = () => filteredTonTheoKien;

  // Về trang 1 khi bộ lọc / dữ liệu / số dòng mỗi trang thay đổi
  useEffect(() => {
    setKienPage(1);
  }, [filteredTonTheoKien, kienPerPage]);

  const kienTotalPages = Math.max(1, Math.ceil(filteredTonTheoKien.length / kienPerPage));
  const kienStartIndex = (kienPage - 1) * kienPerPage;
  const pagedTonTheoKien = filteredTonTheoKien.slice(kienStartIndex, kienStartIndex + kienPerPage);

  // Tính tổng khối lượng của tonTheoKien đã lọc
  const getTotalKhoiLuong = (data) => {
    return data.reduce((sum, item) => sum + (parseFloat(item['SO_KHOI']) || 0), 0);
  };

  // Handle view details - switch to "Chi tiết theo kiện" tab with filters
  const handleViewDetails = (tieuChuan = null, doDay = null) => {
    setActiveTab('theo-kien');

    if (tieuChuan) {
      setFilterTieuChuan([tieuChuan]);
    }

    if (doDay) {
      setFilterDoDay([doDay]);
    }

    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Clear all filters in "Chi tiết theo kiện" tab (only detail filters, not report filters)
  const clearAllFilters = () => {
    setFilterDoDay([]);
    setFilterTieuChuan([]);
    setFilterDai([]);
    setFilterRong([]);
    setFilterMaKien('');
    setSearchDoDay('');
    setSearchTieuChuan('');
    setSearchDai('');
    setSearchRong('');
  };

  // Export to Excel functions
  const exportToExcel = (data, filename, sheetName) => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, `${filename}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success(`Đã xuất file ${filename}`);
  };

  const exportTongHop = () => {
    const data = [
      {
        'Chỉ tiêu': 'Tồn đầu kỳ',
        'Số kiện': tongHopData.tonDauKy.kien,
        'Khối lượng (m³)': tongHopData.tonDauKy.m3.toFixed(4)
      },
      {
        'Chỉ tiêu': 'Nhập trong kỳ',
        'Số kiện': tongHopData.nhapTrongKy.kien,
        'Khối lượng (m³)': tongHopData.nhapTrongKy.m3.toFixed(4)
      },
      {
        'Chỉ tiêu': 'Xuất trong kỳ',
        'Số kiện': tongHopData.xuatTrongKy.kien,
        'Khối lượng (m³)': tongHopData.xuatTrongKy.m3.toFixed(4)
      },
      {
        'Chỉ tiêu': 'Tồn cuối kỳ',
        'Số kiện': tongHopData.tonCuoiKy.kien,
        'Khối lượng (m³)': tongHopData.tonCuoiKy.m3.toFixed(4)
      }
    ];
    exportToExcel(data, 'BaoCao_TongHop', 'Tổng hợp');
  };

  const exportNhomHang = () => {
    const data = tonTheoNhomHang.map((item, index) => ({
      'STT': index + 1,
      'Nhóm hàng': item.nhomHang,
      'Chất lượng': item.tieuChuan,
      'Tồn đầu kỳ - Kiện': item.tonDauKy.kien,
      'Tồn đầu kỳ - m³': item.tonDauKy.m3.toFixed(4),
      'Nhập - Kiện': item.nhap.kien,
      'Nhập - m³': item.nhap.m3.toFixed(4),
      'Xuất - Kiện': item.xuat.kien,
      'Xuất - m³': item.xuat.m3.toFixed(4),
      'Tồn cuối kỳ - Kiện': item.tonCuoiKy.kien,
      'Tồn cuối kỳ - m³': item.tonCuoiKy.m3.toFixed(4)
    }));
    exportToExcel(data, 'BaoCao_TheoNhomHang', 'Theo nhóm hàng');
  };

  const exportDoDay = () => {
    const data = tonTheoDoDay.map((item, index) => ({
      'STT': index + 1,
      'Độ dày (mm)': item.doDay,
      'Tồn đầu kỳ - Kiện': item.tonDauKy.kien,
      'Tồn đầu kỳ - m³': item.tonDauKy.m3.toFixed(4),
      'Nhập - Kiện': item.nhap.kien,
      'Nhập - m³': item.nhap.m3.toFixed(4),
      'Xuất - Kiện': item.xuat.kien,
      'Xuất - m³': item.xuat.m3.toFixed(4),
      'Tồn cuối kỳ - Kiện': item.tonCuoiKy.kien,
      'Tồn cuối kỳ - m³': item.tonCuoiKy.m3.toFixed(4)
    }));
    exportToExcel(data, 'BaoCao_TheoDoDay', 'Theo độ dày');
  };

  const exportTheoKien = () => {
    const filteredData = getFilteredTonTheoKien();
    const data = filteredData.map((item, index) => ({
      'STT': index + 1,
      'Mã kiện': item['MA_KIEN'],
      'Nhóm hàng': item['NHOM_HANG'],
      'Chất lượng': item['TIEU_CHUAN'],
      'Độ dày (mm)': item['DAY'],
      'Số thanh': parseFloat(item['THANH']),
      'Khối lượng (m³)': parseFloat(item['SO_KHOI']).toFixed(4),
      'Ngày nhập': formatDate(item['NGAY_NHAP_XUAT']),
      'Trạng thái': item.trangThai === 'TON_DAU_KY' ? 'Tồn đầu kỳ' : 'Còn tồn'
    }));
    exportToExcel(data, 'BaoCao_ChiTietTheoKien', 'Chi tiết theo kiện');
  };

  const exportTop10 = () => {
    const currentData = activeTop10Tab === 'nhap' ? top10Data.nhapNhieu :
      activeTop10Tab === 'xuat' ? top10Data.xuatNhieu :
        top10Data.tonNhieu;

    const title = activeTop10Tab === 'nhap' ? 'Nhập nhiều nhất' :
      activeTop10Tab === 'xuat' ? 'Xuất nhiều nhất' :
        'Tồn nhiều nhất';

    const data = currentData.map((item, index) => ({
      'Top': index + 1,
      'Nhóm hàng': item.nhomHang,
      'Chất lượng': item.tieuChuan,
      'Số kiện': item.kien,
      'Khối lượng (m³)': item.m3.toFixed(4)
    }));

    exportToExcel(data, `Top10_${title.replace(/ /g, '_')}`, title);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('vi-VN').format(Math.round(num));
  };

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(num);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('vi-VN');
    } catch (error) {
      return dateString;
    }
  };

  // Custom label for bar chart
  const renderCustomBarLabel = (props) => {
    const { x, y, width, height, value } = props;
    return (
      <text
        x={x + width / 2}
        y={y - 5}
        fill="#374151"
        textAnchor="middle"
        fontSize="12"
        fontWeight="600"
      >
        {value}
      </text>
    );
  };

  return (
    <div className="p-3 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      <div className="mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-3 md:p-4 mb-4 border border-gray-100">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-3 gap-3">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-md">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-gray-800">Báo Cáo Tồn Kho</h1>
                <p className="text-xs md:text-sm text-gray-500 mt-0.5">Thống kê tồn đầu kỳ, nhập, xuất, tồn cuối kỳ</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => fetchData(true)}
                disabled={isLoading}
                className="px-3 py-1.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 flex items-center gap-1.5 transition-all shadow-sm hover:shadow text-sm disabled:opacity-50"
                title="Tải lại dữ liệu"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Làm mới</span>
              </button>
              <button
                onClick={() => setShowFilter(!showFilter)}
                className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-all shadow-sm hover:shadow text-sm ${showFilter
                  ? 'text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100'
                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                  }`}
              >
                <Filter className="w-4 h-4" />
                <span className="hidden sm:inline">{showFilter ? 'Ẩn bộ lọc' : 'Hiện bộ lọc'}</span>
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilter && (
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-3 rounded-lg border border-gray-200 shadow-sm">
              <h3 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                <Filter className="w-3.5 h-3.5" />
                Bộ lọc báo cáo:
              </h3>

              <div className="grid grid-cols-2 md:grid-cols-6 gap-2.5">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Từ ngày:</label>
                  <input
                    type="date"
                    value={filterDateFrom}
                    onChange={(e) => setFilterDateFrom(e.target.value)}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Đến ngày:</label>
                  <input
                    type="date"
                    value={filterDateTo}
                    onChange={(e) => setFilterDateTo(e.target.value)}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
                  />
                </div>
                {/* Độ dày filter */}
                <div className="relative" ref={doDayDropdownRef}>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Độ dày:</label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowDoDayDropdown(!showDoDayDropdown)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm bg-white text-left flex items-center justify-between"
                    >
                      <span className={filterDoDay.length === 0 ? 'text-gray-500' : 'text-gray-900 font-medium'}>
                        {getSelectedDoDayText()}
                      </span>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showDoDayDropdown ? 'transform rotate-180' : ''}`} />
                    </button>

                    {showDoDayDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
                        <div className="p-2 border-b border-gray-200 sticky top-0 bg-white">
                          <div className="relative">
                            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="text"
                              value={searchDoDay}
                              onChange={(e) => setSearchDoDay(e.target.value)}
                              placeholder="Tìm độ dày..."
                              className="w-full pl-8 pr-8 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                            {searchDoDay && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSearchDoDay('');
                                }}
                                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="max-h-60 overflow-y-auto">
                          <button
                            onClick={() => {
                              setFilterDoDay([]);
                              setSearchDoDay('');
                            }}
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-purple-50 transition-colors flex items-center justify-between ${filterDoDay.length === 0 ? 'bg-purple-50 text-purple-700 font-semibold' : 'text-gray-700'}`}
                          >
                            <span>Tất cả độ dày</span>
                          </button>
                          {filteredDoDay.map((doDay, index) => (
                            <button
                              key={index}
                              onClick={() => {
                                const newSelection = filterDoDay.includes(doDay)
                                  ? filterDoDay.filter(item => item !== doDay)
                                  : [...filterDoDay, doDay];
                                setFilterDoDay(newSelection);
                              }}
                              className={`w-full px-3 py-2 text-left text-sm hover:bg-purple-50 transition-colors flex items-center gap-2 ${filterDoDay.includes(doDay) ? 'bg-purple-50 text-purple-700 font-semibold' : 'text-gray-700'}`}
                            >
                              <input
                                type="checkbox"
                                checked={filterDoDay.includes(doDay)}
                                onChange={() => { }} // Handle in button onClick
                                className="rounded text-purple-600 focus:ring-purple-500"
                              />
                              <span>{doDay}mm</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {filterDoDay.length > 0 && (
                    <button
                      onClick={() => {
                        setFilterDoDay([]);
                        setSearchDoDay('');
                      }}
                      className="absolute right-8 top-[30px] text-gray-400 hover:text-gray-600"
                      title="Xóa lựa chọn"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Rộng filter */}
                <div className="relative" ref={rongDropdownRef}>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Rộng:</label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowRongDropdown(!showRongDropdown)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm bg-white text-left flex items-center justify-between"
                    >
                      <span className={filterRong.length === 0 ? 'text-gray-500' : 'text-gray-900 font-medium'}>
                        {getSelectedRongText()}
                      </span>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showRongDropdown ? 'transform rotate-180' : ''}`} />
                    </button>

                    {showRongDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
                        <div className="p-2 border-b border-gray-200 sticky top-0 bg-white">
                          <div className="relative">
                            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="text"
                              value={searchRong}
                              onChange={(e) => setSearchRong(e.target.value)}
                              placeholder="Tìm rộng..."
                              className="w-full pl-8 pr-8 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                            {searchRong && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSearchRong('');
                                }}
                                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="max-h-60 overflow-y-auto">
                          <button
                            onClick={() => {
                              setFilterRong([]);
                              setSearchRong('');
                            }}
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-green-50 transition-colors flex items-center justify-between ${filterRong.length === 0 ? 'bg-green-50 text-green-700 font-semibold' : 'text-gray-700'}`}
                          >
                            <span>Tất cả rộng</span>
                          </button>
                          {filteredRong.map((rong, index) => (
                            <button
                              key={index}
                              onClick={() => {
                                const newSelection = filterRong.includes(rong)
                                  ? filterRong.filter(item => item !== rong)
                                  : [...filterRong, rong];
                                setFilterRong(newSelection);
                              }}
                              className={`w-full px-3 py-2 text-left text-sm hover:bg-green-50 transition-colors flex items-center gap-2 ${filterRong.includes(rong) ? 'bg-green-50 text-green-700 font-semibold' : 'text-gray-700'}`}
                            >
                              <input
                                type="checkbox"
                                checked={filterRong.includes(rong)}
                                onChange={() => { }} // Handle in button onClick
                                className="rounded text-green-600 focus:ring-green-500"
                              />
                              <span>{rong}mm</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {filterRong.length > 0 && (
                    <button
                      onClick={() => {
                        setFilterRong([]);
                        setSearchRong('');
                      }}
                      className="absolute right-8 top-[30px] text-gray-400 hover:text-gray-600"
                      title="Xóa lựa chọn"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Dài filter */}
                <div className="relative" ref={daiDropdownRef}>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Dài:</label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowDaiDropdown(!showDaiDropdown)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm bg-white text-left flex items-center justify-between"
                    >
                      <span className={filterDai.length === 0 ? 'text-gray-500' : 'text-gray-900 font-medium'}>
                        {getSelectedDaiText()}
                      </span>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showDaiDropdown ? 'transform rotate-180' : ''}`} />
                    </button>

                    {showDaiDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
                        <div className="p-2 border-b border-gray-200 sticky top-0 bg-white">
                          <div className="relative">
                            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="text"
                              value={searchDai}
                              onChange={(e) => setSearchDai(e.target.value)}
                              placeholder="Tìm dài..."
                              className="w-full pl-8 pr-8 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                            {searchDai && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSearchDai('');
                                }}
                                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="max-h-60 overflow-y-auto">
                          <button
                            onClick={() => {
                              setFilterDai([]);
                              setSearchDai('');
                            }}
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-orange-50 transition-colors flex items-center justify-between ${filterDai.length === 0 ? 'bg-orange-50 text-orange-700 font-semibold' : 'text-gray-700'}`}
                          >
                            <span>Tất cả dài</span>
                          </button>
                          {filteredDai.map((dai, index) => (
                            <button
                              key={index}
                              onClick={() => {
                                const newSelection = filterDai.includes(dai)
                                  ? filterDai.filter(item => item !== dai)
                                  : [...filterDai, dai];
                                setFilterDai(newSelection);
                              }}
                              className={`w-full px-3 py-2 text-left text-sm hover:bg-orange-50 transition-colors flex items-center gap-2 ${filterDai.includes(dai) ? 'bg-orange-50 text-orange-700 font-semibold' : 'text-gray-700'}`}
                            >
                              <input
                                type="checkbox"
                                checked={filterDai.includes(dai)}
                                onChange={() => { }} // Handle in button onClick
                                className="rounded text-orange-600 focus:ring-orange-500"
                              />
                              <span>{dai}mm</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {filterDai.length > 0 && (
                    <button
                      onClick={() => {
                        setFilterDai([]);
                        setSearchDai('');
                      }}
                      className="absolute right-8 top-[30px] text-gray-400 hover:text-gray-600"
                      title="Xóa lựa chọn"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Tiêu chuẩn filter */}
                <div className="relative" ref={tieuChuanDropdownRef}>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Tiêu chuẩn:</label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowTieuChuanDropdown(!showTieuChuanDropdown)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm bg-white text-left flex items-center justify-between"
                    >
                      <span className={filterTieuChuan.length === 0 ? 'text-gray-500' : 'text-gray-900 font-medium'}>
                        {getSelectedTieuChuanText()}
                      </span>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showTieuChuanDropdown ? 'transform rotate-180' : ''}`} />
                    </button>

                    {showTieuChuanDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
                        <div className="p-2 border-b border-gray-200 sticky top-0 bg-white">
                          <div className="relative">
                            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="text"
                              value={searchTieuChuan}
                              onChange={(e) => setSearchTieuChuan(e.target.value)}
                              placeholder="Tìm tiêu chuẩn..."
                              className="w-full pl-8 pr-8 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                            {searchTieuChuan && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSearchTieuChuan('');
                                }}
                                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="max-h-60 overflow-y-auto">
                          <button
                            onClick={() => {
                              setFilterTieuChuan([]);
                              setSearchTieuChuan('');
                            }}
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-indigo-50 transition-colors flex items-center justify-between ${filterTieuChuan.length === 0 ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'text-gray-700'}`}
                          >
                            <span>Tất cả chất lượng</span>
                          </button>
                          {filteredTieuChuan.map((tieuChuan, index) => (
                            <button
                              key={index}
                              onClick={() => {
                                const newSelection = filterTieuChuan.includes(tieuChuan)
                                  ? filterTieuChuan.filter(item => item !== tieuChuan)
                                  : [...filterTieuChuan, tieuChuan];
                                setFilterTieuChuan(newSelection);
                              }}
                              className={`w-full px-3 py-2 text-left text-sm hover:bg-indigo-50 transition-colors flex items-center gap-2 ${filterTieuChuan.includes(tieuChuan) ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'text-gray-700'}`}
                            >
                              <input
                                type="checkbox"
                                checked={filterTieuChuan.includes(tieuChuan)}
                                onChange={() => { }} // Handle in button onClick
                                className="rounded text-indigo-600 focus:ring-indigo-500"
                              />
                              <span>{tieuChuan}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {filterTieuChuan.length > 0 && (
                    <button
                      onClick={() => {
                        setFilterTieuChuan([]);
                        setSearchTieuChuan('');
                      }}
                      className="absolute right-8 top-[30px] text-gray-400 hover:text-gray-600"
                      title="Xóa lựa chọn"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Tab Navigation */}
          <div className="mt-4 flex gap-2 border-b border-gray-200 overflow-x-auto">
            <button
              onClick={() => setActiveTab('tong-hop')}
              className={`px-4 py-2 text-sm font-semibold whitespace-nowrap transition-colors ${activeTab === 'tong-hop'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
                }`}
            >
              Tổng hợp
            </button>
            <button
              onClick={() => setActiveTab('nhom-hang')}
              className={`px-4 py-2 text-sm font-semibold whitespace-nowrap transition-colors ${activeTab === 'nhom-hang'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
                }`}
            >
              Theo nhóm hàng
            </button>
            <button
              onClick={() => setActiveTab('do-day')}
              className={`px-4 py-2 text-sm font-semibold whitespace-nowrap transition-colors ${activeTab === 'do-day'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
                }`}
            >
              Theo độ dày
            </button>
            <button
              onClick={() => setActiveTab('theo-kien')}
              className={`px-4 py-2 text-sm font-semibold whitespace-nowrap transition-colors ${activeTab === 'theo-kien'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
                }`}
            >
              Chi tiết theo kiện
            </button>
            <button
              onClick={() => setActiveTab('domino-18')}
              className={`px-4 py-2 text-sm font-semibold whitespace-nowrap transition-colors ${activeTab === 'domino-18'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
                }`}
            >
              Domino - 18
            </button>
          </div>
        </div>

        {/* Nhắc kiểm tra các dòng xuất kho bất thường: kiện bị xuất trùng, hoặc xuất kiện chưa từng nhập */}
        {dongXuatCanKiemTra.length > 0 && (
          <div className="mb-4 bg-amber-50 border border-amber-300 rounded-lg p-4">
            <div className="flex items-start gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-amber-900">
                  Có {dongXuatCanKiemTra.length} kiện xuất kho cần kiểm tra lại
                </h3>
                <p className="text-xs text-amber-800 mt-1">
                  Những kiện dưới đây được ghi xuất nhưng sổ sách chưa khớp. Vào mục <span className="font-semibold">Quản lý xuất nhập kho</span>, mở phiếu tương ứng để sửa hoặc xóa dòng bị sai. Số liệu tồn kho vẫn tính bình thường, nhưng nên xử lý sớm để sổ sách chính xác.
                </p>
              </div>
            </div>
            <div className="overflow-x-auto max-h-56 overflow-y-auto border border-amber-200 rounded bg-white">
              <table className="min-w-full text-xs">
                <thead className="bg-amber-100 text-amber-900 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Cần kiểm tra</th>
                    <th className="px-3 py-2 text-left font-semibold">Số phiếu xuất</th>
                    <th className="px-3 py-2 text-left font-semibold">Mã kiện</th>
                    <th className="px-3 py-2 text-right font-semibold">Khối lượng (m³)</th>
                    <th className="px-3 py-2 text-left font-semibold">Ngày xuất</th>
                    <th className="px-3 py-2 text-left font-semibold">Ghi chú</th>
                  </tr>
                </thead>
                <tbody>
                  {dongXuatCanKiemTra.map((item, idx) => (
                    <tr key={idx} className="border-t border-amber-100">
                      <td className="px-3 py-2">
                        <span className="inline-block px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium whitespace-nowrap">
                          {item['_loi']}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-medium text-gray-800">{item['SOPHIEU']}</td>
                      <td className="px-3 py-2 font-medium text-gray-800">{item['MA_KIEN']}</td>
                      <td className="px-3 py-2 text-right text-gray-700">{(parseFloat(item['SO_KHOI']) || 0).toFixed(4)}</td>
                      <td className="px-3 py-2 text-gray-700">{formatDate(item['NGAY_NHAP_XUAT'])}</td>
                      <td className="px-3 py-2 text-gray-600">{item['_huongDan']}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Statistics Cards */}
        {activeTab !== 'domino-18' && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-orange-700">TỒN ĐẦU KỲ</h3>
                <Archive className="w-5 h-5 text-orange-600" />
              </div>
              <div className="space-y-1">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-orange-600">Khối lượng:</span>
                  <span className="text-lg font-bold text-orange-900">{tongHopData.tonDauKy.m3.toFixed(4)} m³</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-orange-600">Số kiện:</span>
                  <span className="text-sm font-semibold text-orange-800">{formatNumber(tongHopData.tonDauKy.kien)}</span>
                </div>

                {/* <div className="flex justify-between items-center">
                  <span className="text-xs text-orange-600">Giá trị ƯL:</span>
                  <span className="text-sm font-semibold text-orange-800">{formatNumber(tongHopData.tonDauKy.giaTri)} ₫</span>
                </div> */}
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-green-700">NHẬP TRONG KỲ</h3>
                <ArrowDownToLine className="w-5 h-5 text-green-600" />
              </div>
              <div className="space-y-1">

                <div className="flex justify-between items-center">
                  <span className="text-xs text-green-600">Khối lượng:</span>
                  <span className="text-lg font-bold text-green-900">{tongHopData.nhapTrongKy.m3.toFixed(4)} m³</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-green-600">Số kiện:</span>
                  <span className="text-sm font-semibold text-green-800">{formatNumber(tongHopData.nhapTrongKy.kien)}</span>
                </div>

                {/* <div className="flex justify-between items-center">
                  <span className="text-xs text-green-600">Giá trị ƯL:</span>
                  <span className="text-sm font-semibold text-green-800">{formatNumber(tongHopData.nhapTrongKy.giaTri)} ₫</span>
                </div> */}
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-blue-700">XUẤT TRONG KỲ</h3>
                <ArrowUpFromLine className="w-5 h-5 text-blue-600" />
              </div>
              <div className="space-y-1">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-blue-600">Khối lượng:</span>
                  <span className="text-lg font-bold text-blue-900">{tongHopData.xuatTrongKy.m3.toFixed(4)} m³</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-blue-600">Số kiện:</span>
                  <span className="text-sm font-semibold text-blue-800">{formatNumber(tongHopData.xuatTrongKy.kien)}</span>
                </div>

                {/* <div className="flex justify-between items-center">
                  <span className="text-xs text-blue-600">Giá trị ƯL:</span>
                  <span className="text-sm font-semibold text-blue-800">{formatNumber(tongHopData.xuatTrongKy.giaTri)} ₫</span>
                </div> */}
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-purple-700">TỒN CUỐI KỲ</h3>
                <Archive className="w-5 h-5 text-purple-600" />
              </div>
              <div className="space-y-1">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-purple-600">Khối lượng:</span>
                  <span className="text-lg font-bold text-purple-900">{tongHopData.tonCuoiKy.m3.toFixed(4)} m³</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-purple-600">Số kiện:</span>
                  <span className="text-sm font-semibold text-purple-800">{formatNumber(tongHopData.tonCuoiKy.kien)}</span>
                </div>

                {/* <div className="flex justify-between items-center">
                  <span className="text-xs text-purple-600">Giá trị ƯL:</span>
                  <span className="text-sm font-semibold text-purple-800">{formatNumber(tongHopData.tonCuoiKy.giaTri)} ₫</span>
                </div> */}
              </div>
            </div>
          </div>
        )}

        {/* Tab Content */}
        {activeTab === 'tong-hop' && (
          <div className="space-y-4">
            {/* Chart Tổng Hợp */}
            <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-100">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-base font-bold text-gray-800">Biểu đồ tổng hợp</h3>
                <button
                  onClick={exportTongHop}
                  className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1.5 transition-all shadow-sm hover:shadow text-sm"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  Xuất Excel
                </button>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 text-center">Số kiện</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={[
                        { name: 'Tồn đầu kỳ', value: tongHopData.tonDauKy.kien },
                        { name: 'Nhập', value: tongHopData.nhapTrongKy.kien },
                        { name: 'Xuất', value: tongHopData.xuatTrongKy.kien },
                        { name: 'Tồn cuối kỳ', value: tongHopData.tonCuoiKy.kien }
                      ]}
                      margin={{ top: 30, right: 30, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" fontSize={11} />
                      <YAxis fontSize={10} />
                      <Tooltip />
                      <Bar dataKey="value" fill="#8b5cf6" label={renderCustomBarLabel} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-gray-50 p-3 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 text-center">Khối lượng (m³)</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={[
                        { name: 'Tồn đầu kỳ', value: parseFloat(tongHopData.tonDauKy.m3.toFixed(4)) },
                        { name: 'Nhập', value: parseFloat(tongHopData.nhapTrongKy.m3.toFixed(4)) },
                        { name: 'Xuất', value: parseFloat(tongHopData.xuatTrongKy.m3.toFixed(4)) },
                        { name: 'Tồn cuối kỳ', value: parseFloat(tongHopData.tonCuoiKy.m3.toFixed(4)) }
                      ]}
                      margin={{ top: 30, right: 30, left: 20, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" fontSize={11} />
                      <YAxis fontSize={10} />
                      <Tooltip />
                      <Bar
                        dataKey="value"
                        fill="#3b82f6"
                        label={(props) => {
                          const { x, y, width, value } = props;
                          return (
                            <text
                              x={x + width / 2}
                              y={y - 5}
                              fill="#374151"
                              textAnchor="middle"
                              fontSize="12"
                              fontWeight="600"
                            >
                              {value.toFixed(4)}
                            </text>
                          );
                        }}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Top 10 Section */}
            <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-100">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
                  <Award className="w-5 h-5 text-yellow-500" />
                  Top 10 Mặt Hàng
                </h3>
                <button
                  onClick={exportTop10}
                  className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1.5 transition-all shadow-sm hover:shadow text-sm"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  Xuất Excel
                </button>
              </div>

              {/* Top 10 Tabs */}
              <div className="flex gap-2 mb-4 border-b border-gray-200">
                <button
                  onClick={() => setActiveTop10Tab('nhap')}
                  className={`px-4 py-2 text-sm font-semibold transition-colors ${activeTop10Tab === 'nhap'
                    ? 'text-green-600 border-b-2 border-green-600'
                    : 'text-gray-600 hover:text-gray-800'
                    }`}
                >
                  Nhập nhiều nhất
                </button>
                <button
                  onClick={() => setActiveTop10Tab('xuat')}
                  className={`px-4 py-2 text-sm font-semibold transition-colors ${activeTop10Tab === 'xuat'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-800'
                    }`}
                >
                  Xuất nhiều nhất
                </button>
                <button
                  onClick={() => setActiveTop10Tab('ton')}
                  className={`px-4 py-2 text-sm font-semibold transition-colors ${activeTop10Tab === 'ton'
                    ? 'text-purple-600 border-b-2 border-purple-600'
                    : 'text-gray-600 hover:text-gray-800'
                    }`}
                >
                  Tồn nhiều nhất
                </button>
              </div>

              {/* Top 10 Content */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className={`${activeTop10Tab === 'nhap' ? 'bg-gradient-to-r from-green-50 to-green-100' :
                      activeTop10Tab === 'xuat' ? 'bg-gradient-to-r from-blue-50 to-blue-100' :
                        'bg-gradient-to-r from-purple-50 to-purple-100'
                      }`}>
                      <tr>
                        <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase">Top</th>
                        <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase">Nhóm hàng</th>
                        <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase">Chất lượng</th>
                        <th className={`px-3 py-3 text-center text-xs font-bold uppercase ${activeTop10Tab === 'nhap' ? 'text-green-700' :
                          activeTop10Tab === 'xuat' ? 'text-blue-700' :
                            'text-purple-700'
                          }`}>Kiện</th>
                        <th className={`px-3 py-3 text-center text-xs font-bold uppercase ${activeTop10Tab === 'nhap' ? 'text-green-700' :
                          activeTop10Tab === 'xuat' ? 'text-blue-700' :
                            'text-purple-700'
                          }`}>m³</th>
                        <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase">Chi tiết</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(activeTop10Tab === 'nhap' ? top10Data.nhapNhieu :
                        activeTop10Tab === 'xuat' ? top10Data.xuatNhieu :
                          top10Data.tonNhieu).map((item, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-3 py-2 text-center">
                                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${index === 0 ? 'bg-yellow-400 text-yellow-900' :
                                  index === 1 ? 'bg-gray-300 text-gray-900' :
                                    index === 2 ? 'bg-orange-400 text-orange-900' :
                                      activeTop10Tab === 'nhap' ? 'bg-green-100 text-green-800' :
                                        activeTop10Tab === 'xuat' ? 'bg-blue-100 text-blue-800' :
                                          'bg-purple-100 text-purple-800'
                                  }`}>
                                  {index + 1}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-sm font-medium text-gray-900">{item.nhomHang}</td>
                              <td className="px-3 py-2 text-center">
                                <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded text-xs font-medium">
                                  {item.tieuChuan}
                                </span>
                              </td>
                              <td className={`px-3 py-2 text-sm text-center font-semibold ${activeTop10Tab === 'nhap' ? 'text-green-600' :
                                activeTop10Tab === 'xuat' ? 'text-blue-600' :
                                  'text-purple-600'
                                }`}>{formatNumber(item.kien)}</td>
                              <td className={`px-3 py-2 text-sm text-center font-bold ${activeTop10Tab === 'nhap' ? 'text-green-700' :
                                activeTop10Tab === 'xuat' ? 'text-blue-700' :
                                  'text-purple-700'
                                }`}>{item.m3.toFixed(4)}</td>
                              <td className="px-3 py-2 text-center">
                                <button
                                  onClick={() => handleViewDetails(item.tieuChuan)}
                                  className="p-1.5 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                                  title="Xem chi tiết"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                    </tbody>
                  </table>
                </div>

                {/* Chart */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                      data={(activeTop10Tab === 'nhap' ? top10Data.nhapNhieu :
                        activeTop10Tab === 'xuat' ? top10Data.xuatNhieu :
                          top10Data.tonNhieu).map((item, index) => ({
                            name: `${index + 1}`,
                            nhomHang: item.nhomHang,
                            value: parseFloat(item.m3.toFixed(4))
                          }))}
                      margin={{ top: 30, right: 30, left: 20, bottom: 80 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="nhomHang"
                        fontSize={10}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        interval={0}
                      />
                      <YAxis
                        fontSize={10}
                        label={{ value: 'm³', angle: -90, position: 'insideLeft' }}
                      />
                      <Tooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            return (
                              <div className="bg-white p-2 border border-gray-300 rounded shadow-lg">
                                <p className="text-xs font-semibold">{payload[0].payload.nhomHang}</p>
                                <p className="text-xs text-gray-600">m³: {payload[0].value.toFixed(4)}</p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar dataKey="value" fill={
                        activeTop10Tab === 'nhap' ? '#10b981' :
                          activeTop10Tab === 'xuat' ? '#3b82f6' :
                            '#8b5cf6'
                      }
                        label={{
                          position: 'top',
                          fontSize: 11,
                          fontWeight: 'bold',
                          fill: '#374151',
                          formatter: (value) => value.toFixed(4)
                        }}
                      >
                        {(activeTop10Tab === 'nhap' ? top10Data.nhapNhieu :
                          activeTop10Tab === 'xuat' ? top10Data.xuatNhieu :
                            top10Data.tonNhieu).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'nhom-hang' && (
          <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-100">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                <Package className="w-5 h-5 text-blue-600" />
                Tồn kho theo Nhóm hàng & Chất lượng
              </h2>
              <button
                onClick={exportNhomHang}
                className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1.5 transition-all shadow-sm hover:shadow text-sm"
              >
                <FileSpreadsheet className="w-4 h-4" />
                Xuất Excel
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <tr>
                    <th rowSpan="2" className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase border-r">STT</th>
                    <th rowSpan="2" className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase border-r">Nhóm hàng</th>
                    <th rowSpan="2" className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase border-r">Chất lượng</th>
                    <th colSpan="2" className="px-3 py-2 text-center text-xs font-bold text-orange-700 uppercase border-r bg-orange-50">Tồn đầu kỳ</th>
                    <th colSpan="2" className="px-3 py-2 text-center text-xs font-bold text-green-700 uppercase border-r bg-green-50">Nhập</th>
                    <th colSpan="2" className="px-3 py-2 text-center text-xs font-bold text-blue-700 uppercase border-r bg-blue-50">Xuất</th>
                    <th colSpan="2" className="px-3 py-2 text-center text-xs font-bold text-purple-700 uppercase bg-purple-50">Tồn cuối kỳ</th>
                    <th rowSpan="2" className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase">Chi tiết</th>
                  </tr>
                  <tr>
                    <th className="px-3 py-2 text-center text-xs font-bold text-orange-600 uppercase bg-orange-50">Kiện</th>
                    <th className="px-3 py-2 text-center text-xs font-bold text-orange-600 uppercase border-r bg-orange-50">m³</th>
                    <th className="px-3 py-2 text-center text-xs font-bold text-green-600 uppercase bg-green-50">Kiện</th>
                    <th className="px-3 py-2 text-center text-xs font-bold text-green-600 uppercase border-r bg-green-50">m³</th>
                    <th className="px-3 py-2 text-center text-xs font-bold text-blue-600 uppercase bg-blue-50">Kiện</th>
                    <th className="px-3 py-2 text-center text-xs font-bold text-blue-600 uppercase border-r bg-blue-50">m³</th>
                    <th className="px-3 py-2 text-center text-xs font-bold text-purple-600 uppercase bg-purple-50">Kiện</th>
                    <th className="px-3 py-2 text-center text-xs font-bold text-purple-600 uppercase bg-purple-50">m³</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {tonTheoNhomHang.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-center text-sm text-gray-900">{index + 1}</td>
                      <td className="px-3 py-2 text-sm font-medium text-gray-900">{item.nhomHang}</td>
                      <td className="px-3 py-2 text-center">
                        <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded text-xs font-medium">
                          {item.tieuChuan}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center text-sm text-orange-600 font-semibold bg-orange-50">{formatNumber(item.tonDauKy.kien)}</td>
                      <td className="px-3 py-2 text-center text-sm text-orange-700 font-bold bg-orange-50 border-r">{item.tonDauKy.m3.toFixed(4)}</td>
                      <td className="px-3 py-2 text-center text-sm text-green-600 font-semibold bg-green-50">{formatNumber(item.nhap.kien)}</td>
                      <td className="px-3 py-2 text-center text-sm text-green-700 font-bold bg-green-50 border-r">{item.nhap.m3.toFixed(4)}</td>
                      <td className="px-3 py-2 text-center text-sm text-blue-600 font-semibold bg-blue-50">{formatNumber(item.xuat.kien)}</td>
                      <td className="px-3 py-2 text-center text-sm text-blue-700 font-bold bg-blue-50 border-r">{item.xuat.m3.toFixed(4)}</td>
                      <td className="px-3 py-2 text-center text-sm text-purple-600 font-semibold bg-purple-50">{formatNumber(item.tonCuoiKy.kien)}</td>
                      <td className="px-3 py-2 text-center text-sm text-purple-700 font-bold bg-purple-50">{item.tonCuoiKy.m3.toFixed(4)}</td>
                      <td className="px-3 py-2 text-center">
                        <button
                          onClick={() => handleViewDetails(item.tieuChuan)}
                          className="p-1.5 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                          title="Xem chi tiết"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-100">
                  <tr className="font-bold">
                    <td colSpan="3" className="px-3 py-3 text-center text-sm text-gray-900">TỔNG CỘNG</td>
                    <td className="px-3 py-3 text-center text-sm text-orange-700 bg-orange-50">
                      {formatNumber(tonTheoNhomHang.reduce((sum, item) => sum + item.tonDauKy.kien, 0))}
                    </td>
                    <td className="px-3 py-3 text-center text-sm text-orange-700 bg-orange-50 border-r">
                      {tonTheoNhomHang.reduce((sum, item) => sum + item.tonDauKy.m3, 0).toFixed(4)}
                    </td>
                    <td className="px-3 py-3 text-center text-sm text-green-700 bg-green-50">
                      {formatNumber(tonTheoNhomHang.reduce((sum, item) => sum + item.nhap.kien, 0))}
                    </td>
                    <td className="px-3 py-3 text-center text-sm text-green-700 bg-green-50">
                      {tonTheoNhomHang.reduce((sum, item) => sum + item.nhap.m3, 0).toFixed(4)}
                    </td>
                    <td className="px-3 py-3 text-center text-sm text-blue-700 bg-blue-50">
                      {formatNumber(tonTheoNhomHang.reduce((sum, item) => sum + item.xuat.kien, 0))}
                    </td>
                    <td className="px-3 py-3 text-center text-sm text-blue-700 bg-blue-50 border-r">
                      {tonTheoNhomHang.reduce((sum, item) => sum + item.xuat.m3, 0).toFixed(4)}
                    </td>
                    <td className="px-3 py-3 text-center text-sm text-purple-700 bg-purple-50">
                      {formatNumber(tonTheoNhomHang.reduce((sum, item) => sum + item.tonCuoiKy.kien, 0))}
                    </td>
                    <td className="px-3 py-3 text-center text-sm text-purple-700 bg-purple-50">
                      {tonTheoNhomHang.reduce((sum, item) => sum + item.tonCuoiKy.m3, 0).toFixed(4)}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'do-day' && (
          <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-100">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                <Layers className="w-5 h-5 text-indigo-600" />
                Tồn kho theo Độ dày
              </h2>
              <button
                onClick={exportDoDay}
                className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1.5 transition-all shadow-sm hover:shadow text-sm"
              >
                <FileSpreadsheet className="w-4 h-4" />
                Xuất Excel
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <tr>
                    <th rowSpan="2" className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase border-r">STT</th>
                    <th rowSpan="2" className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase border-r">Độ dày (mm)</th>
                    <th colSpan="2" className="px-3 py-2 text-center text-xs font-bold text-orange-700 uppercase border-r bg-orange-50">Tồn đầu kỳ</th>
                    <th colSpan="2" className="px-3 py-2 text-center text-xs font-bold text-green-700 uppercase border-r bg-green-50">Nhập</th>
                    <th colSpan="2" className="px-3 py-2 text-center text-xs font-bold text-blue-700 uppercase border-r bg-blue-50">Xuất</th>
                    <th colSpan="2" className="px-3 py-2 text-center text-xs font-bold text-purple-700 uppercase border-r bg-purple-50">Tồn cuối kỳ</th>
                    <th rowSpan="2" className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase border-r">Đơn giá ƯL (₫/m³)</th>
                    <th rowSpan="2" className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase border-r">Giá trị ƯL (₫)</th>
                    <th rowSpan="2" className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase">Chi tiết</th>
                  </tr>
                  <tr>
                    <th className="px-3 py-2 text-center text-xs font-bold text-orange-600 uppercase bg-orange-50">Kiện</th>
                    <th className="px-3 py-2 text-center text-xs font-bold text-orange-600 uppercase border-r bg-orange-50">m³</th>
                    <th className="px-3 py-2 text-center text-xs font-bold text-green-600 uppercase bg-green-50">Kiện</th>
                    <th className="px-3 py-2 text-center text-xs font-bold text-green-600 uppercase border-r bg-green-50">m³</th>
                    <th className="px-3 py-2 text-center text-xs font-bold text-blue-600 uppercase bg-blue-50">Kiện</th>
                    <th className="px-3 py-2 text-center text-xs font-bold text-blue-600 uppercase border-r bg-blue-50">m³</th>
                    <th className="px-3 py-2 text-center text-xs font-bold text-purple-600 uppercase bg-purple-50">Kiện</th>
                    <th className="px-3 py-2 text-center text-xs font-bold text-purple-600 uppercase border-r bg-purple-50">m³</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {tonTheoDoDay.map((item, index) => {
                    const isExpanded = expandedDoDayRows[item.doDay] || false;
                    const toggleExpand = () => {
                      setExpandedDoDayRows(prev => ({
                        ...prev,
                        [item.doDay]: !prev[item.doDay]
                      }));
                    };

                    return (
                      <React.Fragment key={index}>
                        {/* Main row */}
                        <tr className="hover:bg-gray-50 bg-gray-50 font-semibold">
                          <td className="px-3 py-2 text-center text-sm text-gray-900">
                            <div className="flex items-center justify-center gap-2">
                              {item.subGroupsArray && item.subGroupsArray.length > 0 && (
                                <button
                                  onClick={toggleExpand}
                                  className="p-1 hover:bg-gray-200 rounded transition-colors"
                                  title={isExpanded ? "Thu gọn" : "Mở rộng"}
                                >
                                  <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                </button>
                              )}
                              {index + 1}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm font-bold">
                              {item.doDay}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-center text-sm text-orange-600 font-semibold bg-orange-50">{formatNumber(item.tonDauKy.kien)}</td>
                          <td className="px-3 py-2 text-center text-sm text-orange-700 font-bold bg-orange-50 border-r">{item.tonDauKy.m3.toFixed(4)}</td>
                          <td className="px-3 py-2 text-center text-sm text-green-600 font-semibold bg-green-50">{formatNumber(item.nhap.kien)}</td>
                          <td className="px-3 py-2 text-center text-sm text-green-700 font-bold bg-green-50 border-r">{item.nhap.m3.toFixed(4)}</td>
                          <td className="px-3 py-2 text-center text-sm text-blue-600 font-semibold bg-blue-50">{formatNumber(item.xuat.kien)}</td>
                          <td className="px-3 py-2 text-center text-sm text-blue-700 font-bold bg-blue-50 border-r">{item.xuat.m3.toFixed(4)}</td>
                          <td className="px-3 py-2 text-center text-sm text-purple-600 font-semibold bg-purple-50">{formatNumber(item.tonCuoiKy.kien)}</td>
                          <td className="px-3 py-2 text-center text-sm text-purple-700 font-bold bg-purple-50 border-r">{item.tonCuoiKy.m3.toFixed(4)}</td>
                          <td className="px-3 py-2 text-center text-sm text-gray-500 border-r">-</td>
                          <td className="px-3 py-2 text-right text-sm font-bold text-green-700 border-r">
                            {item.tonCuoiKy.giaTri > 0 ? formatNumber(Math.round(item.tonCuoiKy.giaTri)) : '-'}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <button
                              onClick={() => handleViewDetails(null, item.doDay)}
                              className="p-1.5 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                              title="Xem chi tiết"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>

                        {/* Sub-rows (quality breakdown) */}
                        {isExpanded && item.subGroupsArray && item.subGroupsArray.map((subGroup, subIndex) => (
                          <tr key={`${index}-sub-${subIndex}`} className="bg-blue-50 hover:bg-blue-100">
                            <td className="px-3 py-2 text-center text-xs text-gray-500"></td>
                            <td className="px-3 py-2 pl-8 text-left text-xs text-gray-700">
                              <span className="px-2 py-1 bg-blue-200 text-blue-900 rounded text-xs font-medium">
                                {subGroup.tieuChuan}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-center text-xs text-orange-600 bg-orange-50">{formatNumber(subGroup.tonDauKy.kien)}</td>
                            <td className="px-3 py-2 text-center text-xs text-orange-700 bg-orange-50 border-r">{subGroup.tonDauKy.m3.toFixed(4)}</td>
                            <td className="px-3 py-2 text-center text-xs text-green-600 bg-green-50">{formatNumber(subGroup.nhap.kien)}</td>
                            <td className="px-3 py-2 text-center text-xs text-green-700 bg-green-50 border-r">{subGroup.nhap.m3.toFixed(4)}</td>
                            <td className="px-3 py-2 text-center text-xs text-blue-600 bg-blue-50">{formatNumber(subGroup.xuat.kien)}</td>
                            <td className="px-3 py-2 text-center text-xs text-blue-700 bg-blue-50 border-r">{subGroup.xuat.m3.toFixed(4)}</td>
                            <td className="px-3 py-2 text-center text-xs text-purple-600 bg-purple-50">{formatNumber(subGroup.tonCuoiKy.kien)}</td>
                            <td className="px-3 py-2 text-center text-xs text-purple-700 bg-purple-50 border-r">{subGroup.tonCuoiKy.m3.toFixed(4)}</td>
                            <td className="px-3 py-2 text-right text-xs font-semibold text-blue-700 border-r">
                              {subGroup.donGia > 0 ? formatNumber(subGroup.donGia) : '-'}
                            </td>
                            <td className="px-3 py-2 text-right text-xs font-semibold text-green-700 border-r">
                              {subGroup.tonCuoiKy.giaTri > 0 ? formatNumber(Math.round(subGroup.tonCuoiKy.giaTri)) : '-'}
                            </td>
                            <td className="px-3 py-2"></td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot className="bg-gray-100">
                  <tr className="font-bold">
                    <td colSpan="2" className="px-3 py-3 text-center text-sm text-gray-900">TỔNG CỘNG</td>
                    <td className="px-3 py-3 text-center text-sm text-orange-700 bg-orange-50">
                      {formatNumber(tonTheoDoDay.reduce((sum, item) => sum + item.tonDauKy.kien, 0))}
                    </td>
                    <td className="px-3 py-3 text-center text-sm text-orange-700 bg-orange-50 border-r">
                      {tonTheoDoDay.reduce((sum, item) => sum + item.tonDauKy.m3, 0).toFixed(4)}
                    </td>
                    <td className="px-3 py-3 text-center text-sm text-green-700 bg-green-50">
                      {formatNumber(tonTheoDoDay.reduce((sum, item) => sum + item.nhap.kien, 0))}
                    </td>
                    <td className="px-3 py-3 text-center text-sm text-green-700 bg-green-50 border-r">
                      {tonTheoDoDay.reduce((sum, item) => sum + item.nhap.m3, 0).toFixed(4)}
                    </td>
                    <td className="px-3 py-3 text-center text-sm text-blue-700 bg-blue-50">
                      {formatNumber(tonTheoDoDay.reduce((sum, item) => sum + item.xuat.kien, 0))}
                    </td>
                    <td className="px-3 py-3 text-center text-sm text-blue-700 bg-blue-50 border-r">
                      {tonTheoDoDay.reduce((sum, item) => sum + item.xuat.m3, 0).toFixed(4)}
                    </td>
                    <td className="px-3 py-3 text-center text-sm text-purple-700 bg-purple-50">
                      {formatNumber(tonTheoDoDay.reduce((sum, item) => sum + item.tonCuoiKy.kien, 0))}
                    </td>
                    <td className="px-3 py-3 text-center text-sm text-purple-700 bg-purple-50 border-r">
                      {tonTheoDoDay.reduce((sum, item) => sum + item.tonCuoiKy.m3, 0).toFixed(4)}
                    </td>
                    <td className="px-3 py-3 text-center text-sm text-gray-500 border-r">-</td>
                    <td className="px-3 py-3 text-right text-sm font-bold text-green-700 border-r">
                      {formatNumber(Math.round(tonTheoDoDay.reduce((sum, item) => sum + (item.tonCuoiKy.giaTri || 0), 0)))} ₫
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Biểu đồ cho độ dày */}
            <div className="mt-6 bg-gray-50 p-3 rounded-lg">
              <h4 className="text-sm font-semibold text-gray-700 mb-3 text-center">Tồn cuối kỳ theo độ dày (m³)</h4>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={tonTheoDoDay.map(item => ({
                    name: `${item.doDay}mm`,
                    value: parseFloat(item.tonCuoiKy.m3.toFixed(4))
                  }))}
                  margin={{ top: 30, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" fontSize={11} />
                  <YAxis fontSize={10} label={{ value: 'm³', angle: -90, position: 'insideLeft' }} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#6366f1" label={{ position: 'top', fontSize: 11, fontWeight: 'bold' }}>
                    {tonTheoDoDay.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {activeTab === 'theo-kien' && (
          <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-100">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-4 gap-3">
              <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                <Package className="w-5 h-5 text-teal-600" />
                Chi tiết tồn kho theo kiện
                <span className="ml-2 text-sm font-normal text-gray-600">
                  Hiển thị: <span className="font-bold text-teal-600">{formatNumber(getFilteredTonTheoKien().length)}</span> / {formatNumber(tonTheoKien.length)} kiện
                  {' - '}
                  <span className="font-bold text-teal-600">{getTotalKhoiLuong(getFilteredTonTheoKien()).toFixed(4)}</span> / {getTotalKhoiLuong(tonTheoKien).toFixed(4)} m³
                </span>
              </h2>
              <div className="flex gap-2">
                {(filterDoDay !== 'ALL' || filterTieuChuan !== 'ALL' || filterDai !== 'ALL' || filterRong !== 'ALL' || filterMaKien) && (
                  <button
                    onClick={clearAllFilters}
                    className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center gap-1.5 transition-all shadow-sm hover:shadow text-sm"
                  >
                    <X className="w-4 h-4" />
                    Xóa bộ lọc
                  </button>
                )}
                <button
                  onClick={exportTheoKien}
                  className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1.5 transition-all shadow-sm hover:shadow text-sm"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  Xuất Excel
                </button>
              </div>
            </div>

            {/* Advanced Filters */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-3 rounded-lg border border-gray-200 shadow-sm mb-4">
              <h3 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                <Filter className="w-3.5 h-3.5" />
                Bộ lọc chi tiết:
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                {/* Độ dày filter */}
                <div className="relative" ref={doDayDropdownRef}>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Độ dày:</label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowDoDayDropdown(!showDoDayDropdown)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white text-left flex items-center justify-between"
                    >
                      <span className={filterDoDay === 'ALL' ? 'text-gray-500' : 'text-gray-900'}>
                        {getSelectedDoDayText()}
                      </span>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showDoDayDropdown ? 'transform rotate-180' : ''}`} />
                    </button>

                    {showDoDayDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
                        <div className="p-2 border-b border-gray-200 sticky top-0 bg-white">
                          <div className="relative">
                            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="text"
                              value={searchDoDay}
                              onChange={(e) => setSearchDoDay(e.target.value)}
                              placeholder="Tìm kiếm..."
                              className="w-full pl-8 pr-8 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                            {searchDoDay && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSearchDoDay('');
                                }}
                                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="max-h-48 overflow-y-auto">
                          <button
                            onClick={() => {
                              setFilterDoDay('ALL');
                              setShowDoDayDropdown(false);
                              setSearchDoDay('');
                            }}
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-blue-50 transition-colors ${filterDoDay === 'ALL' ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700'}`}
                          >
                            Tất cả
                          </button>

                          {filteredDoDay.map((doDay, index) => (
                            <button
                              key={index}
                              onClick={() => {
                                setFilterDoDay(doDay);
                                setShowDoDayDropdown(false);
                                setSearchDoDay('');
                              }}
                              className={`w-full px-3 py-2 text-left text-sm hover:bg-blue-50 transition-colors ${filterDoDay === doDay ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700'}`}
                            >
                              {doDay}mm
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Chất lượng filter */}
                <div className="relative" ref={tieuChuanDropdownRef}>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Chất lượng:</label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowTieuChuanDropdown(!showTieuChuanDropdown)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white text-left flex items-center justify-between"
                    >
                      <span className={filterTieuChuan === 'ALL' ? 'text-gray-500' : 'text-gray-900'}>
                        {getSelectedTieuChuanText()}
                      </span>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showTieuChuanDropdown ? 'transform rotate-180' : ''}`} />
                    </button>

                    {showTieuChuanDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
                        <div className="p-2 border-b border-gray-200 sticky top-0 bg-white">
                          <div className="relative">
                            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="text"
                              value={searchTieuChuan}
                              onChange={(e) => setSearchTieuChuan(e.target.value)}
                              placeholder="Tìm kiếm..."
                              className="w-full pl-8 pr-8 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                            {searchTieuChuan && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSearchTieuChuan('');
                                }}
                                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="max-h-48 overflow-y-auto">
                          <button
                            onClick={() => {
                              setFilterTieuChuan('ALL');
                              setShowTieuChuanDropdown(false);
                              setSearchTieuChuan('');
                            }}
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-blue-50 transition-colors ${filterTieuChuan === 'ALL' ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700'}`}
                          >
                            Tất cả
                          </button>

                          {filteredTieuChuan.map((tieuChuan, index) => (
                            <button
                              key={index}
                              onClick={() => {
                                setFilterTieuChuan(tieuChuan);
                                setShowTieuChuanDropdown(false);
                                setSearchTieuChuan('');
                              }}
                              className={`w-full px-3 py-2 text-left text-sm hover:bg-blue-50 transition-colors ${filterTieuChuan === tieuChuan ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700'}`}
                            >
                              {tieuChuan}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Mã kiện search */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Mã kiện:</label>
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={filterMaKien}
                      onChange={(e) => setFilterMaKien(e.target.value)}
                      placeholder="Tìm kiếm mã kiện..."
                      className="w-full pl-8 pr-8 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
                    />
                    {filterMaKien && (
                      <button
                        onClick={() => setFilterMaKien('')}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <tr>
                    <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase">STT</th>
                    <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase">Mã kiện</th>
                    <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase">Nhóm hàng</th>
                    <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase">Chất lượng</th>
                    <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase">Độ dày</th>
                    <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase">Số thanh</th>
                    <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase">Khối lượng (m³)</th>
                    <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase">Đơn giá ƯL (₫/m³)</th>
                    <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase">Giá trị ƯL (₫)</th>
                    <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase">Ngày nhập</th>
                    <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase">Trạng thái</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pagedTonTheoKien.map((item, index) => (
                    <tr key={kienStartIndex + index} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-center text-sm text-gray-900">{kienStartIndex + index + 1}</td>
                      <td className="px-3 py-2 text-sm font-medium text-gray-900">{item['MA_KIEN']}</td>
                      <td className="px-3 py-2 text-sm text-gray-700">{item['NHOM_HANG']}</td>
                      <td className="px-3 py-2 text-center">
                        <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded text-xs font-medium">
                          {item['TIEU_CHUAN']}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-semibold">
                          {item['DAY']}mm
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center text-sm font-bold text-indigo-700">
                        {parseFloat(item['THANH'])}
                      </td>
                      <td className="px-3 py-2 text-center text-sm font-bold text-teal-700">
                        {parseFloat(item['SO_KHOI']).toFixed(4)}
                      </td>
                      <td className="px-3 py-2 text-right text-sm font-semibold text-blue-700">
                        {item.DON_GIA_UOC_LUONG > 0 ? formatNumber(item.DON_GIA_UOC_LUONG) : '-'}
                      </td>
                      <td className="px-3 py-2 text-right text-sm font-bold text-green-700">
                        {item.GIA_TRI_UOC_LUONG > 0 ? formatNumber(Math.round(item.GIA_TRI_UOC_LUONG)) : '-'}
                      </td>
                      <td className="px-3 py-2 text-center text-sm text-gray-600">
                        {formatDate(item['NGAY_NHAP_XUAT'])}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {item.trangThai === 'TON_DAU_KY' ? (
                          <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-semibold">
                            Tồn đầu kỳ
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                            Còn tồn
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gradient-to-r from-gray-100 to-gray-200">
                  <tr className="font-bold">
                    <td colSpan="5" className="px-3 py-3 text-center text-sm text-gray-900">TỔNG CỘNG</td>
                    <td className="px-3 py-3 text-center text-sm text-indigo-700">
                      {formatNumber(getFilteredTonTheoKien().reduce((sum, item) => sum + (parseFloat(item['THANH']) || 0), 0))}
                    </td>
                    <td className="px-3 py-3 text-center text-sm text-teal-700">
                      {getFilteredTonTheoKien().reduce((sum, item) => sum + (parseFloat(item['SO_KHOI']) || 0), 0).toFixed(4)} m³
                    </td>
                    <td className="px-3 py-3 text-center text-sm text-gray-500">-</td>
                    <td className="px-3 py-3 text-right text-sm font-bold text-green-700">
                      {formatNumber(Math.round(getFilteredTonTheoKien().reduce((sum, item) => sum + (item.GIA_TRI_UOC_LUONG || 0), 0)))} ₫
                    </td>
                    <td colSpan="2" className="px-3 py-3 text-center text-sm text-gray-900">
                      {formatNumber(getFilteredTonTheoKien().length)} kiện
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Phân trang */}
            {filteredTonTheoKien.length > 0 && (
              <div className="mt-3 flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <span>Hiển thị</span>
                  <select
                    value={kienPerPage}
                    onChange={(e) => setKienPerPage(Number(e.target.value))}
                    className="px-2 py-1 border border-gray-300 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    {[20, 50, 100, 200, 500].map(n => (
                      <option key={n} value={n}>{n} kiện</option>
                    ))}
                  </select>
                  <span>
                    {formatNumber(kienStartIndex + 1)} - {formatNumber(Math.min(kienStartIndex + kienPerPage, filteredTonTheoKien.length))} / {formatNumber(filteredTonTheoKien.length)}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {[
                    { label: '«', page: 1, title: 'Trang đầu' },
                    { label: '‹', page: kienPage - 1, title: 'Trang trước' },
                  ].map(btn => (
                    <button
                      key={btn.title}
                      onClick={() => setKienPage(btn.page)}
                      disabled={kienPage === 1}
                      title={btn.title}
                      className="min-w-[32px] px-2 py-1 rounded-lg border text-xs border-gray-300 text-gray-700 hover:bg-gray-50 disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      {btn.label}
                    </button>
                  ))}
                  <span className="px-2 text-xs text-gray-700">
                    Trang <span className="font-semibold">{kienPage}</span> / {kienTotalPages}
                  </span>
                  {[
                    { label: '›', page: kienPage + 1, title: 'Trang sau' },
                    { label: '»', page: kienTotalPages, title: 'Trang cuối' },
                  ].map(btn => (
                    <button
                      key={btn.title}
                      onClick={() => setKienPage(btn.page)}
                      disabled={kienPage === kienTotalPages}
                      title={btn.title}
                      className="min-w-[32px] px-2 py-1 rounded-lg border text-xs border-gray-300 text-gray-700 hover:bg-gray-50 disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      {btn.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tab Domino - 18 */}
        {activeTab === 'domino-18' && (
          <div className="space-y-4">
            {/* Tổng hợp Domino */}
            <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-100">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-base font-bold text-gray-800">Tổng hợp Domino - 18</h3>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-blue-50 to-blue-100">
                    <tr>
                      <th rowSpan="2" className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase border-r">Chỉ tiêu</th>
                      <th colSpan="2" className="px-3 py-2 text-center text-xs font-bold text-blue-700 uppercase bg-blue-50">Tồn đầu kỳ</th>
                      <th colSpan="2" className="px-3 py-2 text-center text-xs font-bold text-green-700 uppercase bg-green-50">Nhập trong kỳ</th>
                      <th colSpan="2" className="px-3 py-2 text-center text-xs font-bold text-red-700 uppercase bg-red-50">Xuất trong kỳ</th>
                      <th colSpan="2" className="px-3 py-2 text-center text-xs font-bold text-purple-700 uppercase bg-purple-50">Tồn cuối kỳ</th>
                    </tr>
                    <tr>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 bg-blue-50">Kiện</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 bg-blue-50 border-r">KL (kg)</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 bg-green-50">Kiện</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 bg-green-50 border-r">KL (kg)</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 bg-red-50">Kiện</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 bg-red-50 border-r">KL (kg)</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 bg-purple-50">Kiện</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 bg-purple-50">KL (kg)</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr className="hover:bg-gray-50">
                      <td className="px-3 py-3 text-sm text-gray-900 font-medium border-r">Domino - 18</td>
                      <td className="px-3 py-3 text-sm text-center text-blue-600 font-semibold bg-blue-50">{formatNumber(tongHopDataDomino.tonDauKy.kien)}</td>
                      <td className="px-3 py-3 text-sm text-center text-blue-600 font-semibold bg-blue-50 border-r">{formatNumber(tongHopDataDomino.tonDauKy.kl.toFixed(0))}</td>
                      <td className="px-3 py-3 text-sm text-center text-green-600 font-semibold bg-green-50">{formatNumber(tongHopDataDomino.nhapTrongKy.kien)}</td>
                      <td className="px-3 py-3 text-sm text-center text-green-600 font-semibold bg-green-50 border-r">{formatNumber(tongHopDataDomino.nhapTrongKy.kl.toFixed(0))}</td>
                      <td className="px-3 py-3 text-sm text-center text-red-600 font-semibold bg-red-50">{formatNumber(tongHopDataDomino.xuatTrongKy.kien)}</td>
                      <td className="px-3 py-3 text-sm text-center text-red-600 font-semibold bg-red-50 border-r">{formatNumber(tongHopDataDomino.xuatTrongKy.kl.toFixed(0))}</td>
                      <td className="px-3 py-3 text-sm text-center text-purple-600 font-semibold bg-purple-50">{formatNumber(tongHopDataDomino.tonCuoiKy.kien)}</td>
                      <td className="px-3 py-3 text-sm text-center text-purple-600 font-semibold bg-purple-50">{formatNumber(tongHopDataDomino.tonCuoiKy.kl.toFixed(0))}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Chi tiết theo kiện Domino */}
            <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-100">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
                  <Package className="w-5 h-5 text-teal-600" />
                  Chi tiết tồn kho Domino - 18
                  <span className="ml-2 text-sm font-normal text-gray-600">
                    Tổng số: <span className="font-bold text-teal-600">{formatNumber(tonTheoDominoKien.reduce((sum, item) => sum + (parseFloat(item['SO KIEN']) || 0), 0).toFixed(0))}</span> kiện
                    {' - '}
                    <span className="font-bold text-teal-600">{formatNumber(tonTheoDominoKien.reduce((sum, item) => sum + (parseFloat(item['TONG KG UL']) || 0), 0).toFixed(0))}</span> kg
                  </span>
                </h3>
                <button
                  onClick={() => {
                    const data = tonTheoDominoKien.map(item => ({
                      'Số phiếu': item['SOPHIEU'] || '',
                      'Tiêu chuẩn': item['TIEU_CHUAN'] || '',
                      'Đội hàng kho': item['DOI_HANG_KHO'] || '',
                      'Số kiện': parseFloat(item['SO KIEN']) || 0,
                      'KL (kg)': parseFloat(item['TONG KG UL']) || 0,
                      'Nghiệp vụ': item['NGHIEP_VU'] || '',
                      'Ngày': item['NGAY_NHAP_XUAT'] || ''
                    }));
                    const ws = XLSX.utils.json_to_sheet(data);
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, 'Domino-18');
                    XLSX.writeFile(wb, 'BaoCao_Domino18.xlsx');
                  }}
                  className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1.5 transition-all shadow-sm hover:shadow text-sm"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  Xuất Excel
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase">STT</th>
                      <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase">Số phiếu</th>
                      <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase">Tiêu chuẩn</th>
                      <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase">Đội hàng kho</th>
                      <th className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase">Số kiện</th>
                      <th className="px-3 py-3 text-right text-xs font-bold text-gray-700 uppercase">KL (kg)</th>
                      <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase">Nghiệp vụ</th>
                      <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase">Ngày</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tonTheoDominoKien.length > 0 ? (
                      tonTheoDominoKien.map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-3 py-3 text-sm text-gray-700">{index + 1}</td>
                          <td className="px-3 py-3 text-sm text-gray-900 font-medium">{item['SOPHIEU'] || '—'}</td>
                          <td className="px-3 py-3 text-sm text-gray-700">{item['TIEU_CHUAN'] || '—'}</td>
                          <td className="px-3 py-3 text-sm text-gray-700">{item['DOI_HANG_KHO'] || '—'}</td>
                          <td className="px-3 py-3 text-sm text-right text-gray-900 font-semibold">{formatNumber((parseFloat(item['SO KIEN']) || 0).toFixed(0))}</td>
                          <td className="px-3 py-3 text-sm text-right text-gray-900 font-semibold">{formatNumber((parseFloat(item['TONG KG UL']) || 0).toFixed(0))}</td>
                          <td className="px-3 py-3 text-center">
                            <span className={`px-2 py-1 text-xs rounded-full ${item['NGHIEP_VU'] === 'NHAP'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                              }`}>
                              {item['NGHIEP_VU'] || '—'}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-sm text-center text-gray-700">
                            {item['NGAY_NHAP_XUAT'] ? new Date(item['NGAY_NHAP_XUAT']).toLocaleDateString('vi-VN') : '—'}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="px-3 py-6 text-center text-sm text-gray-500">
                          Không có dữ liệu tồn kho Domino - 18
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot className="bg-gradient-to-r from-gray-100 to-gray-200">
                    <tr>
                      <td colSpan="4" className="px-3 py-3 text-sm font-bold text-gray-800 text-right">Tổng cộng:</td>
                      <td className="px-3 py-3 text-sm font-bold text-right text-gray-900">
                        {formatNumber(tonTheoDominoKien.reduce((sum, item) => sum + (parseFloat(item['SO KIEN']) || 0), 0).toFixed(0))}
                      </td>
                      <td className="px-3 py-3 text-sm font-bold text-right text-gray-900">
                        {formatNumber(tonTheoDominoKien.reduce((sum, item) => sum + (parseFloat(item['TONG KG UL']) || 0), 0).toFixed(0))}
                      </td>
                      <td colSpan="2"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        )}

      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default BaoCaoKho;

